import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Copy, Pencil, Ban, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

export default function AuditTemplatesTab({ canManage, refreshTick, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [constants, setConstants] = useState({ categories: [], cqc_key_questions: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audits-qa/templates", { params: { active_only: !showInactive } });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => { api.get("/audits-qa/constants").then((r) => setConstants(r.data)).catch(() => {}); }, []);

  const dup = async (row) => {
    try { await api.post(`/audits-qa/templates/${row.id}/duplicate`); toast.success("Template duplicated"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deact = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"?`)) return;
    try { await api.delete(`/audits-qa/templates/${row.id}`); toast.success("Template deactivated"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-lg" data-testid="audits-qa-templates-tab">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2"><LayoutTemplate className="w-4 h-4 text-sky-700" /> Audit Templates</h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-xl">Starter templates for common UK care-sector audits. Customise, duplicate or deactivate to fit your service.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="templates-show-inactive" /> Show deactivated</label>
          {canManage && <Button size="sm" onClick={() => setCreating(true)} data-testid="btn-template-new" className="h-8 bg-sky-700 hover:bg-sky-800"><Plus className="w-3.5 h-3.5 mr-1.5" /> New Template</Button>}
        </div>
      </div>
      {loading ? <div className="p-8 text-sm text-slate-500">Loading…</div>
        : (
        <div className="overflow-x-auto">
          <table className="data-table w-full" data-testid="templates-table">
            <thead><tr>
              <th>Template</th><th>Category</th><th>CQC Mapping</th><th>Status</th><th className="w-24 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} data-testid={`template-row-${t.id}`}>
                  <td><div className="font-medium text-slate-900">{t.name}</div>
                    {t.description && <div className="text-[11px] text-slate-500 max-w-md truncate">{t.description}</div>}
                  </td>
                  <td className="text-slate-500">{(constants.categories || []).find((c) => c.key === t.category)?.label || t.category}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(t.cqc_key_questions || []).map((k) => (
                        <span key={k} className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200">{k}</span>
                      ))}
                    </div>
                  </td>
                  <td>{t.is_active ? <span className="text-[11px] text-emerald-700 font-medium">Active</span> : <span className="text-[11px] text-slate-500">Deactivated</span>}</td>
                  <td className="text-right whitespace-nowrap">
                    {canManage && <>
                      <button onClick={() => setEditing(t)} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`btn-template-edit-${t.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => dup(t)} className="p-1 text-slate-500 hover:text-sky-700 ml-0.5" data-testid={`btn-template-duplicate-${t.id}`} title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                      {t.is_active && <button onClick={() => deact(t)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`btn-template-deactivate-${t.id}`} title="Deactivate"><Ban className="w-3.5 h-3.5" /></button>}
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TemplateDialog open={creating || !!editing} template={editing} constants={constants}
        onOpenChange={(v) => { if (!v) { setEditing(null); setCreating(false); } }}
        onSaved={() => { setEditing(null); setCreating(false); load(); onChanged?.(); }} />
    </section>
  );
}

function TemplateDialog({ open, template, constants, onOpenChange, onSaved }) {
  const [form, setForm] = useState(defaults());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...defaults(), ...(template || {}) }); }, [open, template]);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleFive = (k) => patch("cqc_key_questions", (form.cqc_key_questions || []).includes(k) ? form.cqc_key_questions.filter((x) => x !== k) : [...(form.cqc_key_questions || []), k]);
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      if (template?.id) await api.patch(`/audits-qa/templates/${template.id}`, form);
      else await api.post("/audits-qa/templates", form);
      toast.success(template?.id ? "Template updated" : "Template created");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{template?.id ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><FL label="Name"><Input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="template-input-name" /></FL></div>
          <FL label="Category">
            <select value={form.category} onChange={(e) => patch("category", e.target.value)} data-testid="template-input-category" className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
              {(constants.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </FL>
          <div />
          <div className="col-span-2">
            <FL label="CQC Five Key Questions">
              <div className="flex flex-wrap gap-2 mt-1">
                {(constants.cqc_key_questions || []).map((k) => (
                  <label key={k} className="flex items-center gap-1 text-xs text-slate-700">
                    <input type="checkbox" checked={(form.cqc_key_questions || []).includes(k)} onChange={() => toggleFive(k)} data-testid={`template-kq-${k}`} />
                    {k}
                  </label>
                ))}
              </div>
            </FL>
          </div>
          <div className="col-span-2"><FL label="Description"><Textarea rows={3} value={form.description || ""} onChange={(e) => patch("description", e.target.value)} /></FL></div>
          <div className="col-span-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.is_active} onChange={(e) => patch("is_active", e.target.checked)} /> <span className="text-slate-700">Active</span></div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-template-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaults() { return { name: "", category: "other", cqc_key_questions: [], cqc_quality_statements: [], description: "", is_active: true, checklist: [] }; }
function FL({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span><div className="mt-0.5">{children}</div></label>; }
